import React, { useEffect, useState } from 'react';
import { TextButton } from 'components/v2/buttons';
import WizardSelectComponent from 'components/v2/epd-wizard-inputs/WizardSelect';
import { LabelGroup } from 'components/v2/form/styled';
import { HelpBox } from 'components/v2/help-boxes';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import { useTranslation } from 'react-i18next';
import { DataSourceModel, DataSourceVersionModel, EPDDataSourceModel, EPDModel } from 'services/EpdClient';
import { DoubleFieldGrid, FieldContainer, FieldPanelInput } from 'styles/v2/Styles.styled';
import DataSourceVersionsService from 'services/DataSourceVersionsService';
import DataSourcesService from 'services/DataSourcesService';
import WizardCheckbox from 'components/v2/epd-wizard-inputs/WizardCheckbox';
import styled from 'styled-components';

interface Props {
  initialDataSources: EPDDataSourceModel[];
  onSave(newValues: EPDDataSourceModel[]): void;
  required: boolean;
}

const DataSourcesCopySection: React.FC<Props> = ({ initialDataSources, onSave, required }) => {
  const [rowDataSourceValues, setDataSourceRowValues] = useState<EPDDataSourceModel[]>(initialDataSources);
  const [dataSources, setDataSources] = useState<DataSourceModel[]>([]);
  const [dataSourceVersions, setDataSourceVersions] = useState<DataSourceVersionModel[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchDataSourceVersions = async () => {
      const res = await DataSourceVersionsService.getDataSourceVersions();
      setDataSourceVersions(res);
    };

    const fetchDataSources = async () => {
      const res = await DataSourcesService.getDataSources();
      setDataSources(res);
    };

    fetchDataSources();
    fetchDataSourceVersions();
  }, []);

  const createDataSourceRow = () => {
    const newRowValues: EPDDataSourceModel[] = [
      ...rowDataSourceValues,
      {
        id: undefined,
        dataSource: { id: undefined, name: '' },
        dataSourceVersion: { id: undefined, name: '' },
        isMain: false,
      },
    ];
    setDataSourceRowValues(newRowValues);
  };

  const deleteDataSourceRow = (index: number) => {
    const newValues = rowDataSourceValues.filter((_, i) => i !== index);
    setDataSourceRowValues(newValues);
    onSave(newValues);
  };

  return (
    <>
      <FieldContainer>
        <FieldPanelInput>
          <LabelGroup>
            <span style={{ fontWeight: 'bold' }}>DATA SOURCES USED FOR THIS DATA SET</span>
            <TooltipHelpIcon content={t('epdWizard.wizardStep2.tooltips.dataSources')} position="right" />
            {required && !rowDataSourceValues?.length && <TooltipErrorIcon content="Required" />}
          </LabelGroup>
          <HelpBox>Select main data source and specific version of the background database used for the EPD.</HelpBox>
        </FieldPanelInput>
      </FieldContainer>

      <DoubleFieldGrid style={{ marginTop: 22 }}>
        {rowDataSourceValues.map((item, index) => (
          <React.Fragment key={index}>
            <FieldPanelInput>
              <WizardSelectComponent
                options={dataSources.map((x) => ({ value: x.id, label: x.name }))}
                value={item?.dataSource?.id ? { value: item?.dataSource?.id, label: item?.dataSource?.name } : null}
                isClearable
                placeholder={t('placeholder.defaultSelectDatabase')}
                name={'DataSource'}
                label="Database"
                tooltip="Background database used for the EPD."
                tooltipPosition="right"
                required
                onChanged={(propName: string, id: string) => {
                  const newDataSources = rowDataSourceValues.map((d, i) =>
                    i === index
                      ? {
                          ...d,
                          dataSource: { id: id, name: dataSources.find((ds) => ds.id === id)?.name },
                          dataSourceVersion: { id: undefined, name: '' },
                        }
                      : d
                  );
                  setDataSourceRowValues(newDataSources);
                  onSave(newDataSources);
                }}
              />

              <CheckBoxGroup>
                <WizardCheckbox
                  inputId={`dataSourceMain${index}`}
                  label="Set as main database"
                  name="isMain"
                  value={item?.isMain}
                  onChanged={(_, value) => {
                    const newDataSources = rowDataSourceValues.map((d, i) => ({ ...d, isMain: value && i === index }));
                    setDataSourceRowValues(newDataSources);
                    onSave(newDataSources);
                  }}
                />
              </CheckBoxGroup>
            </FieldPanelInput>

            <DataSourceVersionColumn>
              <WizardSelectComponent
                options={dataSourceVersions
                  .filter((dataSourceVersionItem) => dataSourceVersionItem.dataSourceId === item?.dataSource?.id)
                  .map((x) => ({ value: x.id, label: x.name, dataSourceId: x.dataSourceId }))}
                value={
                  item?.dataSourceVersion?.id
                    ? { value: item?.dataSourceVersion?.id, label: item?.dataSourceVersion?.name }
                    : null
                }
                isClearable
                name="DataSourceVersion"
                label="Version"
                tooltip="Select specific version of the background database"
                required
                placeholder={t('placeholder.defaultSelectDatabaseVersion')}
                onChanged={(propName: string, id: string) => {
                  const newDataSources = rowDataSourceValues.map((d, i) =>
                    i === index
                      ? {
                          ...d,
                          dataSourceVersion: { id: id, name: dataSourceVersions.find((dv) => dv.id === id)?.name },
                        }
                      : d
                  );
                  setDataSourceRowValues(newDataSources);
                  onSave(newDataSources);
                }}
              />
              {rowDataSourceValues.length > 1 && !item.isMain && (
                <TextButton
                  icon="pi pi-trash"
                  style={{ alignSelf: 'center', marginLeft: '0.5rem' }}
                  onClick={() => deleteDataSourceRow(index)}
                  text
                />
              )}
            </DataSourceVersionColumn>
          </React.Fragment>
        ))}
      </DoubleFieldGrid>
      {rowDataSourceValues.length < 6 && <TextButton label="+ Add data source" onClick={createDataSourceRow} text />}
    </>
  );
};

export default DataSourcesCopySection;

const CheckBoxGroup = styled.div`
  text-align: start;
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  border-radius: 2px;

  & .p-checkbox {
    width: 16px;
    height: 16px;
  }

  & .p-checkbox .p-checkbox-box {
    width: 14px;
    height: 14px;
  }
`;

const DataSourceVersionColumn = styled.div`
  display: flex;
`;
